<template>
  <NcPage>
    <NcHeader title="Update beschikbaar" />

    <NcContent padded>
      <div class="update-modal-content">
        <!-- Update Icon -->
        <div class="update-icon-container">
          <mdicon name="cloud-download-outline" size="72" class="text-primary"></mdicon>
        </div>

        <!-- Version Information -->
        <div class="version-info">
          <h2 class="update-title">Nieuwe versie beschikbaar</h2>
          <div class="version-numbers">
            <div class="version-item">
              <span class="version-label">Huidige versie:</span>
              <span class="version-value">{{ updateInfo?.currentVersion }}</span>
            </div>
            <mdicon name="arrow-right" class="arrow-icon" size="20"></mdicon>
            <div class="version-item">
              <span class="version-label">Nieuwe versie:</span>
              <span class="version-value version-highlight">{{ updateInfo?.newVersion }}</span>
            </div>
          </div>
        </div>

        <!-- Release Notes -->
        <div class="release-notes" v-if="updateInfo?.notes">
          <h3 class="notes-title">Wat is er nieuw?</h3>
          <p class="notes-content">{{ updateInfo.notes }}</p>
        </div>

        <!-- Description -->
        <p class="update-description" v-if="!updateInfo?.notes">
          Er is een nieuwe versie van Novacheck beschikbaar. Update nu om te profiteren van de nieuwste functies en verbeteringen.
        </p>

        <!-- Action Buttons -->
        <div class="button-container">
          <NcButton
            expand="block"
            color="primary"
            size="large"
            class="update-button"
            @click="updateNow">
            <template #start><mdicon name="cloud-download-outline" size="20"></mdicon></template>
            Nu updaten
          </NcButton>

          <NcButton
            expand="block"
            fill="clear"
            color="medium"
            class="remind-button"
            @click="remindLater">
            Herinner me morgen
          </NcButton>
        </div>
      </div>
    </NcContent>
  </NcPage>
</template>

<script>
import {
  modalController
} from '@ionic/vue';
import { Browser } from '@capacitor/browser';
import { mapGetters } from 'vuex';
import updateService from '@/services/updateService';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';

export default {
  name: 'UpdateModal',
  computed: {
    ...mapGetters(['getUpdateInfo']),
    updateInfo() {
      return this.getUpdateInfo;
    },
    storeUrl() {
      if (!this.updateInfo) return '';

      if (this.updateInfo.platform === 'android') {
        return 'https://play.google.com/store/apps/details?id=nl.twolf.novacheck';
      } else if (this.updateInfo.platform === 'ios') {
        return 'https://apps.apple.com/app/novacheck/id6737801823';
      }
      return '';
    }
  },
  async mounted() {
    // Set status bar to match modal background (light)
    if (Capacitor.getPlatform() !== 'web') {
      try {
        await StatusBar.setStyle({ style: Style.Light });
        await StatusBar.setBackgroundColor({ color: '#f5f5f5' });
      } catch (error) {
        console.log('Could not set status bar:', error);
      }
    }
  },
  async beforeUnmount() {
    // Restore status bar to default (dark content on light background)
    if (Capacitor.getPlatform() !== 'web') {
      try {
        await StatusBar.setStyle({ style: Style.Dark });
        await StatusBar.setBackgroundColor({ color: '#ffffff' });
      } catch (error) {
        console.log('Could not restore status bar:', error);
      }
    }
  },
  methods: {
    async updateNow() {
      // Open app store
      if (this.storeUrl) {
        await Browser.open({ url: this.storeUrl });
      }

      // Close modal
      await this.closeModal();
    },
    async remindLater() {
      // Mark as dismissed for today
      updateService.dismissUpdate();

      // Close modal
      await this.closeModal();
    },
    async closeModal() {
      await modalController.dismiss();
    }
  }
};
</script>

<style scoped>
.update-modal-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 0;
  max-width: 500px;
  margin: 0 auto;
}

.update-icon-container {
  margin-bottom: 24px;
}

.update-icon-container mdicon {
  color: var(--ion-color-primary);
}

.version-info {
  width: 100%;
  text-align: center;
  margin-bottom: 24px;
}

.update-title {
  font-size: 24px;
  font-weight: 600;
  color: var(--ion-color-dark);
  margin: 0 0 16px 0;
}

.version-numbers {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px;
  background: var(--ion-color-light);
  border-radius: 12px;
}

.version-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.version-label {
  font-size: 12px;
  color: var(--ion-color-medium);
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.version-value {
  font-size: 18px;
  font-weight: 600;
  color: var(--ion-color-dark);
}

.version-highlight {
  color: var(--ion-color-primary);
}

.arrow-icon {
  font-size: 20px;
  color: var(--ion-color-medium);
}

.release-notes {
  width: 100%;
  margin-bottom: 24px;
  padding: 16px;
  background: var(--ion-color-light);
  border-radius: 12px;
  border-left: 4px solid var(--ion-color-primary);
}

.notes-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--ion-color-dark);
  margin: 0 0 12px 0;
}

.notes-content {
  font-size: 14px;
  line-height: 1.6;
  color: var(--ion-color-dark);
  margin: 0;
  white-space: pre-line;
}

.update-description {
  font-size: 14px;
  line-height: 1.6;
  color: var(--ion-color-medium);
  text-align: center;
  margin: 0 0 32px 0;
}

.button-container {
  width: 100%;
  margin-top: auto;
}

.update-button {
  --border-radius: 12px;
  font-weight: 600;
  margin-bottom: 8px;
  height: 48px;
}

.remind-button {
  height: 40px;
  font-weight: 500;
}
</style>
